import { useState, useRef, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ExternalLink } from 'lucide-react';
import './CitationLink.css';

export type CitationSource = {
  url: string;
  title: string;
  domain: string;
};

type Props = {
  index: number;
  source: CitationSource | undefined;
};

export default function CitationLink({ index, source }: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  // Close popover when clicking outside
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (!source) {
    return <sup className="citation">[{index}]</sup>;
  }

  return (
    <span className="citation-wrapper" ref={ref}>
      <sup
        className="citation citation--active"
        role="button"
        tabIndex={0}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((prev) => !prev);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((prev) => !prev);
          }
        }}
        aria-label={`Source ${index}: ${source.title}`}
      >
        [{index}]
      </sup>
      <AnimatePresence>
        {open && (
          <motion.span
            className="citation__popover"
            initial={{ opacity: 0, y: 4, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 4, scale: 0.95 }}
            transition={{ duration: 0.15 }}
          >
            <span className="citation__title">{source.title}</span>
            <span className="citation__domain">{source.domain}</span>
            <a
              href={source.url}
              target="_blank"
              rel="noopener noreferrer"
              className="citation__link"
              onClick={(e) => e.stopPropagation()}
            >
              <ExternalLink size={10} /> visit
            </a>
          </motion.span>
        )}
      </AnimatePresence>
    </span>
  );
}

/**
 * Replace [N] citation markers in text with CitationLink components.
 * Returns an array of strings and CitationLink elements.
 */
export function renderCitations(
  text: string,
  sources: CitationSource[],
): (string | JSX.Element)[] {
  const parts: (string | JSX.Element)[] = [];
  const regex = /\[(\d+)\]/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const num = parseInt(match[1], 10);
    const source = sources[num - 1]; // [1]-indexed
    parts.push(
      <CitationLink key={`cite-${match.index}`} index={num} source={source} />,
    );
    lastIndex = regex.lastIndex;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
}
